import { useCallback, useMemo } from 'react';

import { dateWithTime } from 'utils/date';
import { round } from 'utils/math';

import { useLocale } from '../useSitecoreContext';

/**
 * Format a date relative to now, like 'tomorrow' or '2 weeks ago'.
 */
export function useRelativeTimeFormatter() {
	const { locale } = useLocale();

	const formatter = useMemo(
		() => new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }),
		[locale],
	);

	return useCallback(
		(date: string | Date | undefined): string => {
			if (!date) {
				return '';
			}
			const diffInMilliseconds =
				dateWithTime(new Date(date), 12).getTime() -
				dateWithTime(new Date(), 12).getTime();
			const diffInDays = diffInMilliseconds / (1000 * 60 * 60 * 24);
			const absDiffInDays = Math.abs(diffInDays);

			if (absDiffInDays < 7) {
				return formatter.format(round(diffInDays), 'day');
			}
			if (absDiffInDays < 30) {
				return formatter.format(round(diffInDays / 7), 'week');
			}
			if (absDiffInDays < 365) {
				return formatter.format(round(diffInDays / 30), 'month');
			}
			return formatter.format(round(diffInDays / 365), 'year');
		},
		[formatter],
	);
}
